import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { MapPinned, Trash2, Download, Sprout, ExternalLink } from "lucide-react";
import { toast } from "sonner";

// Phase 17-C item 9: growth ops — admin-managed areas (public
// /volunteering-in pages + sitemap), live launch-area config (db override,
// no rebuild) and the interest lists (email + outward code only) with CSV export.
export function GrowthPanel() {
  const [areas, setAreas] = useState([]);
  const [newArea, setNewArea] = useState("");
  const [launch, setLaunch] = useState("");
  const [launchLoaded, setLaunchLoaded] = useState(false);
  const [interest, setInterest] = useState({});
  const [demand, setDemand] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.adminAreas().then(setAreas).catch(() => {});
    api.adminLaunchAreas().then((r) => { setLaunch((r.launch_areas || []).join(", ")); setLaunchLoaded(true); }).catch(() => {});
    api.adminInterestSummary().then((r) => setInterest(r.counts || {})).catch(() => {});
    api.adminInterestDemand().then((r) => setDemand(r.demand || [])).catch(() => {});
  };
  useEffect(load, []);

  const addArea = async (e) => {
    e.preventDefault();
    if (!newArea.trim()) return;
    setBusy(true);
    try {
      await api.adminCreateArea(newArea.trim());
      setNewArea("");
      toast.success("Area page created");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create that area");
    } finally { setBusy(false); }
  };

  const removeArea = async (a) => {
    try {
      await api.adminDeleteArea(a.id);
      toast(`Removed ${a.name}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove that area");
    }
  };

  const saveLaunch = async () => {
    setBusy(true);
    try {
      const r = await api.adminSetLaunchAreas(launch.split(",").map((s) => s.trim()).filter(Boolean));
      setLaunch((r.launch_areas || []).join(", "));
      toast.success(r.launch_areas.length ? "Launch areas updated — the landing banner is live" : "Launch areas cleared — banner hidden");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save launch areas");
    } finally { setBusy(false); }
  };

  const notifyCount = interest.collective_notify || 0;
  const launchCount = interest.launch_area || 0;

  const downloadCsv = async (area) => {
    try {
      const blob = await api.adminInterestExportCsv(null, area);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = area ? `interest-${area.toLowerCase()}.csv` : "interest-list.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="mt-12" data-testid="growth-panel">
      <h2 className="font-display text-2xl text-forest tracking-tight flex items-center gap-2">
        <Sprout className="w-5 h-5 text-terracotta" /> Growth
      </h2>
      <p className="text-neutral-600 text-sm mt-1">Area pages, launch-area banner and interest lists.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
        {/* Area pages */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="growth-areas-card">
          <div className="text-xs uppercase tracking-[0.16em] text-forest/70 font-bold flex items-center gap-1.5">
            <MapPinned className="w-3.5 h-3.5" /> Area pages
          </div>
          <p className="text-[11px] text-neutral-400 mt-1">Public SEO pages at /volunteering-in/&lt;slug&gt; — auto-added to the sitemap.</p>
          <form onSubmit={addArea} className="flex gap-2 mt-3">
            <Input data-testid="growth-area-input" value={newArea} onChange={(e) => setNewArea(e.target.value)} placeholder="e.g. Lambeth" className="h-9 text-sm" />
            <Button type="submit" data-testid="growth-area-add" disabled={busy} variant="outline" className="rounded-full h-9 px-4 text-xs shrink-0">Add</Button>
          </form>
          <div className="mt-3 space-y-1.5">
            {areas.length === 0 && <div className="text-sm text-neutral-500">No area pages yet.</div>}
            {areas.map((a) => (
              <div key={a.id} className="flex items-center justify-between text-sm border-t border-neutral-100 pt-1.5" data-testid={`growth-area-${a.slug}`}>
                <a href={`/volunteering-in/${a.slug}`} target="_blank" rel="noreferrer" className="text-forest hover:underline inline-flex items-center gap-1">
                  {a.name} <ExternalLink className="w-3 h-3 text-neutral-400" />
                </a>
                <button data-testid={`growth-area-delete-${a.slug}`} onClick={() => removeArea(a)} className="text-neutral-400 hover:text-red-600" aria-label={`Remove ${a.name}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Launch areas + interest lists */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="growth-launch-card">
          <div className="text-xs uppercase tracking-[0.16em] text-forest/70 font-bold">Live launch areas</div>
          <p className="text-[11px] text-neutral-400 mt-1">Districts we're live in (comma-separated, e.g. "Lambeth, Southwark"). Empty = no landing banner.</p>
          <div className="flex gap-2 mt-3">
            <Input data-testid="growth-launch-input" value={launch} onChange={(e) => setLaunch(e.target.value)} placeholder="Lambeth, Southwark" className="h-9 text-sm" disabled={!launchLoaded} />
            <Button data-testid="growth-launch-save" onClick={saveLaunch} disabled={busy || !launchLoaded} variant="outline" className="rounded-full h-9 px-4 text-xs shrink-0">Save</Button>
          </div>

          <div className="text-xs uppercase tracking-[0.16em] text-forest/70 font-bold mt-5">Interest lists</div>
          <p className="text-[11px] text-neutral-400 mt-1">Email + postcode district only — nothing else is stored.</p>
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex items-center justify-between" data-testid="growth-interest-notify">
              <span className="text-neutral-600">"Tell me when a collective starts"</span>
              <span className="font-medium text-forest">{notifyCount}</span>
            </div>
            <div className="flex items-center justify-between" data-testid="growth-interest-launch">
              <span className="text-neutral-600">Launch-area waitlist</span>
              <span className="font-medium text-forest">{launchCount}</span>
            </div>
          </div>
          <button
            data-testid="growth-interest-export"
            onClick={() => downloadCsv(null)}
            className="inline-flex items-center gap-1.5 mt-4 text-[11px] text-neutral-500 border border-neutral-200 rounded-full px-3 py-1.5 hover:border-forest hover:text-forest transition-colors"
          >
            <Download className="w-3 h-3" /> Export CSV
          </button>
        </div>
      </div>

      {/* Waitlist demand (2026-02): read-only, outcodes auto-resolved to districts */}
      <div className="bg-white border border-neutral-200 rounded-xl p-5 mt-4" data-testid="waitlist-demand-card">
        <div className="text-xs uppercase tracking-[0.16em] text-forest/70 font-bold">Waitlist demand</div>
        <p className="text-[11px] text-neutral-400 mt-1">
          Where people are waiting — outcodes resolved to districts automatically. Entries are emailed
          once when real activity appears nearby (a collective, or 3+ open opportunities within 5 miles).
        </p>
        {demand.length === 0 ? (
          <div className="text-sm text-neutral-500 mt-3">No waitlist entries yet.</div>
        ) : (
          <table className="w-full mt-3 text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-neutral-400 border-b border-neutral-100">
                <th className="py-1.5 font-medium">District</th>
                <th className="py-1.5 font-medium">Outcodes</th>
                <th className="py-1.5 font-medium text-right">Waiting</th>
                <th className="py-1.5 font-medium text-right">Notified</th>
                <th className="py-1.5 font-medium text-right">Total</th>
                <th className="py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {demand.map((d) => (
                <tr key={d.district} className="border-b border-neutral-50" data-testid={`demand-row-${d.district.replace(/\s+/g, "-").toLowerCase()}`}>
                  <td className="py-2 font-medium text-forest">{d.district}</td>
                  <td className="py-2 text-neutral-500 text-xs">{d.outcodes.join(", ")}</td>
                  <td className="py-2 text-right">{d.waiting}</td>
                  <td className="py-2 text-right text-neutral-500">{d.notified}</td>
                  <td className="py-2 text-right font-medium">{d.total}</td>
                  <td className="py-2 text-right">
                    <button
                      data-testid={`demand-export-${d.district.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => downloadCsv(d.district)}
                      className="text-neutral-400 hover:text-forest"
                      title={`Export ${d.district} CSV`}
                      aria-label={`Export ${d.district} CSV`}
                    >
                      <Download className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
